import { useMemo } from 'react';

import {
  getClosestCategory,
  getSortedTooltipItems,
} from './categorical-tooltip.js';
import { CategoricalBarChartTooltipWrapper } from './CategoricalBarChartTooltipWrapper.js';
import { ChartActionsMenu } from '../../../../core/components/series-action/ChartActionsMenu.js';
import { useChartActionsMenu } from '../../../../core/components/series-action/hooks/useChartActionsMenu.js';
import { mergeTooltipActions } from '../../../../core/components/series-action/utils/mergeTooltipActions.js';
import { InteractiveChartTooltip } from '../../../../core/components/tooltip/InteractiveChartTooltip/components/InteractiveChartTooltip.js';
import { SeriesBasedContent } from '../../../../core/components/tooltip/InteractiveChartTooltip/contents/SeriesBasedContent.js';
import { CategoricalTooltipHeader } from '../../../../core/components/tooltip/InteractiveChartTooltip/headers/CategoricalTooltipHeader.js';
import { useSeriesBasedItemEstimatedSize } from '../../../../core/components/tooltip/InteractiveChartTooltip/hooks/useSeriesBasedItemEstimatedSize.js';
import { useTooltipSeriesActions } from '../../../../core/components/tooltip/shared/hooks/useTooltipSeriesActions.js';
import { Formatter } from '../../../../core/types/formatter.js';
import { TooltipItem } from '../../../../core/types/tooltip.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import { VARIANT_GROUPED } from '../../../constants.js';
import { useTooltipState } from '../../../hooks/store/selectors.js';
import { useCategoricalBarChartScales } from '../../../hooks/useCategoricalBarChartScales.js';
import { useCategoricalBarChartTooltip } from '../../../hooks/useCategoricalBarChartTooltip.js';
import { useCategoricalBarFilteredData } from '../../../hooks/useCategoricalBarFilteredData.js';
import { useTruncationMode } from '../../../hooks/useTruncationMode.js';
import {
  Dimension,
  DimensionXYPoint,
} from '../../../types/categorical-bar-chart-data.js';
import { useSeriesActionClickHandler } from '../hooks/useSeriesActionClickHandler.js';

interface CategoricalBarChartTooltipProps {
  /** Closest point to the mouse position & the x and y position of the tooltip */
  closestDimensionPoint?: DimensionXYPoint;
  hasHeader: boolean;
  formatter: Formatter;
  unit?: string;
}

/**
 * Tooltip specific to the CategoricalBarChart.
 */
export function CategoricalBarChartTooltip({
  closestDimensionPoint,
  hasHeader,
  formatter,
  unit,
}: CategoricalBarChartTooltipProps) {
  const { visibleTooltip } = useTooltipState();
  const { variant } = useCategoricalBarChartTooltip();
  const truncationMode = useTruncationMode();
  const {
    filteredCategories: categories,
    dimensionMap,
    categoryMap,
  } = useCategoricalBarFilteredData();
  const {
    onLeave: handleMenuLeave,
    onScroll: handleMenuScroll,
    selectedItem,
    updateSelectedItem,
    actionsMenuRef,
    isScrolling,
  } = useChartActionsMenu();
  const grouped = variant === VARIANT_GROUPED;
  const closestCategory = useMemo(
    () => getClosestCategory(categories, dimensionMap, closestDimensionPoint),
    [categories, dimensionMap, closestDimensionPoint],
  );
  const { valueScale } = useCategoricalBarChartScales();
  const sortedTooltipItems = useMemo(
    () =>
      getSortedTooltipItems(
        dimensionMap,
        closestDimensionPoint,
        grouped,
        closestCategory,
        formatter,
        valueScale,
        unit,
      ),
    [
      dimensionMap,
      closestDimensionPoint,
      grouped,
      closestCategory,
      formatter,
      valueScale,
      unit,
    ],
  );

  const actionClickHandler = useSeriesActionClickHandler(
    sortedTooltipItems.length > 1,
  );

  const seriesActions = useTooltipSeriesActions<Dimension>(
    closestCategory?.dimensions,
    (dimension) => safeGet(dimensionMap, dimension).id,
  );

  const seriesActionsCallback = (item: TooltipItem) =>
    mergeTooltipActions(item, seriesActions);

  const estimatedItemSize = useSeriesBasedItemEstimatedSize(
    sortedTooltipItems,
    'single-line',
  );

  const showTooltip = sortedTooltipItems.length && visibleTooltip;

  const headerItem = hasHeader
    ? closestDimensionPoint &&
      sortedTooltipItems.find(
        (item) => item.id === closestDimensionPoint.datapoint.id,
      )
    : undefined;

  const bodyItems = headerItem
    ? sortedTooltipItems.filter((item) => item !== headerItem)
    : sortedTooltipItems;

  return showTooltip ? (
    <CategoricalBarChartTooltipWrapper
      onMouseLeave={handleMenuLeave}
      onScrollContent={handleMenuScroll}
      estimatedItemSize={estimatedItemSize}
    >
      <InteractiveChartTooltip.Header>
        {hasHeader && (
          <CategoricalTooltipHeader
            name={safeGet(categoryMap, closestCategory).name}
            item={headerItem}
          />
        )}
      </InteractiveChartTooltip.Header>
      {bodyItems && (
        <InteractiveChartTooltip.Body>
          {bodyItems.map((item, index) => () => {
            const isBodyItem = item !== headerItem;
            return (
              isBodyItem && (
                <ChartActionsMenu
                  key={`${item.id}`}
                  isOpen={selectedItem === item.id}
                  actions={() => seriesActionsCallback(item)()}
                  ref={actionsMenuRef}
                  onActionClick={actionClickHandler}
                >
                  <SeriesBasedContent
                    item={item}
                    onMouseHover={() => updateSelectedItem(item.id)}
                    showDivider={index !== bodyItems.length - 1}
                    truncationMode={truncationMode}
                    isScrolling={isScrolling}
                  />
                </ChartActionsMenu>
              )
            );
          })}
        </InteractiveChartTooltip.Body>
      )}
    </CategoricalBarChartTooltipWrapper>
  ) : null;
}
